import React, { useEffect, useState } from 'react';
import MealPlan from '../models/meal-plan';
import {
  getMealPlans,
  createMealPlan as apiCreateMealPlan,
  updateMealPlan as apiUpdateMealPlan,
  deleteMealPlan as apiDeleteMealPlan,
  addMealToPlan as apiAddMealToPlan,
  removeMealFromPlan as apiRemoveMealFromPlan,
  completeMealPlan as apiCompleteMealPlan,
} from '../api/meal-plan-api';

export interface MealPlansState {
  plans: MealPlan[];
  isLoading: boolean;
  activePlanCount: number;
  refresh: () => Promise<void>;
  createPlan: (name: string, dueDate?: string) => Promise<MealPlan>;
  updatePlan: (id: string, name: string, dueDate: string | null) => Promise<void>;
  deletePlan: (id: string) => Promise<void>;
  addMealToPlan: (planId: string, mealId: string) => Promise<void>;
  removeMealFromPlan: (itemId: string) => Promise<void>;
  completePlan: (planId: string, cookedMealIds: string[], cookedDate: string) => Promise<void>;
}

const initialMealPlansState: MealPlansState = {
  plans: [],
  isLoading: true,
  activePlanCount: 0,
  refresh: async () => {},
  createPlan: async () => ({}) as MealPlan,
  updatePlan: async () => {},
  deletePlan: async () => {},
  addMealToPlan: async () => {},
  removeMealFromPlan: async () => {},
  completePlan: async () => {},
};

export const MealPlansContext = React.createContext<MealPlansState>(initialMealPlansState);

interface MealPlansContextProviderProps {
  children: React.ReactNode;
}

export const MealPlansContextProvider = (props: MealPlansContextProviderProps) => {
  const [plans, setPlans] = useState<MealPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const activePlanCount = plans.filter((p) => p.status === 'active').length;

  const refresh = async () => {
    try {
      const fetchedPlans = await getMealPlans();
      setPlans(fetchedPlans);
    } finally {
      setIsLoading(false);
    }
  };

  const createPlan = async (name: string, dueDate?: string): Promise<MealPlan> => {
    const plan = await apiCreateMealPlan(name, dueDate);
    await refresh();
    return plan;
  };

  const updatePlan = async (id: string, name: string, dueDate: string | null): Promise<void> => {
    await apiUpdateMealPlan(id, name, dueDate);
    await refresh();
  };

  const deletePlan = async (id: string): Promise<void> => {
    await apiDeleteMealPlan(id);
    await refresh();
  };

  const addMealToPlanHandler = async (planId: string, mealId: string): Promise<void> => {
    await apiAddMealToPlan(planId, mealId);
    await refresh();
  };

  const removeMealFromPlanHandler = async (itemId: string): Promise<void> => {
    await apiRemoveMealFromPlan(itemId);
    await refresh();
  };

  const completePlan = async (planId: string, cookedMealIds: string[], cookedDate: string): Promise<void> => {
    await apiCompleteMealPlan(planId, cookedMealIds, cookedDate);
    await refresh();
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <MealPlansContext.Provider
      value={{
        plans,
        isLoading,
        activePlanCount,
        refresh,
        createPlan,
        updatePlan,
        deletePlan,
        addMealToPlan: addMealToPlanHandler,
        removeMealFromPlan: removeMealFromPlanHandler,
        completePlan,
      }}
    >
      {props.children}
    </MealPlansContext.Provider>
  );
};

export default MealPlansContext;
